import { useCurrentUser } from 'components/providers'
import posthog from 'posthog-js'
import { createContext, useContext, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router'
import { IWorkspace, IWorkspaceSubscription } from 'types'
import {
  FetchStatus,
  updateDoc,
  useDoc,
  useMutateWorkspaceRecords,
  usePrefCurrentWorkspace,
} from 'utils'

export interface WorkspaceContextProps {
  status: FetchStatus
  subscription?: IWorkspaceSubscription | null
  updateWorkspace: (body: Partial<IWorkspace>) => Promise<void>
  userCount?: number
  workspace?: IWorkspace | null
}

export const WorkspaceContext = createContext<WorkspaceContextProps>({} as any)

export function WorkspaceProvider({ children }: { children: any }) {
  const user = useCurrentUser()
  const id = useParams().workspaceId
  const { updateWorkspaceRecord } = useMutateWorkspaceRecords()
  const navigate = useNavigate()
  const [_, updateWorkspaceId] = usePrefCurrentWorkspace()
  const [workspace, workspaceStatus] = useDoc('Workspace', id)
  const [subscription, subscriptionStatus] = useDoc('workspaceSubscriptions', id)

  const status = workspaceStatus === 'success' ? subscriptionStatus : workspaceStatus
  const userCount = workspace
    ? workspace.userIds.length + Object.keys(workspace.pendingUserByEmail).length
    : undefined

  useEffect(() => {
    if (workspaceStatus === 'loading') return

    const hasAccess = user?.isAdmin || Boolean(user && workspace?.userById[user.id])
    if ((!workspace && id !== 'all') || !hasAccess) {
      updateWorkspaceId('')
      navigate('/', { replace: true })
      return
    }

    updateWorkspaceId(id ?? '')
  }, [workspace, workspaceStatus])

  useEffect(() => {
    if (!user || status !== 'success' || !workspace) return

    const { plan = 'basic' } = subscription || {}
    const workspaceUser = workspace.userById[user.id]
    const permission = user.isAdmin ? 'admin' : workspaceUser?.permission
    posthog.identify(user.id, {
      permission,
      dateAdded: workspaceUser?.created,
      name: user.name,
      email: user.email,
      entevate: isEntevateEmail(user.email),
    })
    posthog.group('workspace', workspace.id, {
      title: workspace.title,
      plan,
    })
  }, [status, user])

  async function updateWorkspace(body: Partial<IWorkspace>) {
    if (!user || !workspace) return

    const updated = await updateDoc(user!.id, 'Workspace', { ...workspace, ...body })
    updateWorkspaceRecord(updated)
  }

  return (
    <WorkspaceContext.Provider
      value={{ status, subscription, updateWorkspace, userCount, workspace }}
    >
      {children}
    </WorkspaceContext.Provider>
  )
}

export const useWorkspace = () => useContext(WorkspaceContext)

function isEntevateEmail(email: string) {
  if (email.endsWith('@entevate.com')) return true

  switch (email) {
    case 'grantlonie@gmail.com':
    case 'h18shah@gmail.com':
    case 'hanahzac@gmail.com':
    case 'hanahzac@rediffmail.com':
    case 'harsh.shah1@cat.com':
    case 'jake.hamann@cat.com':
      return true
  }

  return false
}
